export const getProductId = (p) => p?.id || p?._id;

export const updateAllProductCaches = (queryClient, productId, updateFn) => {
  // Update ALL product-related queries, not just "products"
  queryClient.setQueriesData(
    {
      predicate: (q) => {
        if (!Array.isArray(q?.queryKey)) return false;
        const key = q.queryKey[0];
        // Include products, trending, and category queries
        return key === "products" || key === "categories";
      },
    },
    (old) => {
      if (!old) return old;

      const updateList = (list) => {
        if (!Array.isArray(list)) return list;
        return list.map((item) => {
          const id = getProductId(item);
          if (id === productId) {
            return {
              ...item,
              quantity: updateFn(item.quantity ?? 0, item),
            };
          }
          return item;
        });
      };

      // SIMPLE ARRAY (trending, category products, global products)
      if (Array.isArray(old)) {
        return updateList(old);
      }

      // PAGINATED (products: [...])
      if (old.products && Array.isArray(old.products)) {
        return { ...old, products: updateList(old.products) };
      }

      // INFINITE QUERY (pages: [...])
      if (old.pages && Array.isArray(old.pages)) {
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            products: updateList(page?.products || []),
          })),
        };
      }

      return old;
    }
  );

  // ALSO update trending products specifically
  queryClient.setQueryData(["products", "trending"], (old) => {
    if (!Array.isArray(old)) return old;
    return old.map((item) => {
      const id = getProductId(item);
      if (id === productId) {
        return {
          ...item,
          quantity: updateFn(item.quantity ?? 0, item),
        };
      }
      return item;
    });
  });

  // Update global products cache
  queryClient.setQueryData(["products", "global"], (old) => {
    if (!Array.isArray(old)) return old;
    return old.map((item) => {
      const id = getProductId(item);
      if (id === productId) {
        return {
          ...item,
          quantity: updateFn(item.quantity ?? 0, item),
        };
      }
      return item;
    });
  });

  // Update category products cache (all category queries)
  queryClient.setQueriesData(
    {
      predicate: (q) =>
        Array.isArray(q?.queryKey) &&
        q.queryKey[0] === "products" &&
        q.queryKey[1] === "category",
    },
    (old) => {
      if (!Array.isArray(old)) return old;
      return old.map((item) => {
        const id = getProductId(item);
        if (id === productId) {
          return {
            ...item,
            quantity: updateFn(item.quantity ?? 0, item),
          };
        }
        return item;
      });
    }
  );
};

export const getProductWithCartQuantity = (product, cartItems) => {
  if (!product) return product;
  const cartItem = cartItems.find(
    (i) => getProductId(i) === getProductId(product)
  );
  return { ...product, quantity: cartItem?.quantity || 0 };
};

export const sanitizeProductName = (name) => {
  return name
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric characters with hyphens
    .replace(/^-+|-+$/g, ""); // Remove leading or trailing hyphens
};

// Get the display name for a product or box
export const getProductName = (item) => {
  if (!item) return "";
  // For products: productName, for boxes: name
  const baseName = item.productName || item.name || "";
  // Add size if available
  return baseName + (item.size ? ` - ${item.size}` : "");
};

// Get the price for a product or box
export const getProductPrice = (item) => {
  if (!item) return 0;
  // For products: price, for boxes: amount
  return item.price || item.amount || 0;
};

// Get the category for a product 
export const getProductCategory = (item) => {
  return item?.category || "";
};

export const APP_BASE_URL =
  process.env.REACT_APP_BASE_URL || "https://app.virtuesshop.co";

const getProductIdentifier = (product) => {
  return product?.sku || getProductId(product);
};

const getProductSlug = (product) => {
  const slug = sanitizeProductName(getProductName(product));
  return slug || "product";
};

export const getProductDetailPath = (product) => {
  const identifier = getProductIdentifier(product);
  if (!identifier) return "";
  const slug = getProductSlug(product);
  return `/products/${slug}/${identifier}`;
};

export const getProductDetailUrl = (product, baseUrl) => {
  const path = getProductDetailPath(product);
  if (!path) return "";
  const origin =
    baseUrl ||
    (typeof window !== "undefined" && window.location?.origin) ||
    APP_BASE_URL;
  return `${origin}${path}`;
};
